<template>
  <div class="download-page container py-4">
    <!-- 標題區 -->
    <div class="mb-4">
      <h2 class="fw-bold text-primary mb-1">
        {{ $t('downloads.title') }}
      </h2>
      <p class="text-muted mb-0 small">
        {{ $t('downloads.subtitle') }}
      </p>
    </div>

    <!-- 狀態列：錯誤訊息 -->
    <div v-if="error" class="alert alert-danger py-2 small">
      {{ error }}
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="text-muted text-center py-5">
      <div class="spinner-border spinner-border-sm mb-3" role="status"></div>
      <div>{{ $t('common.loading') }}</div>
    </div>

    <!-- 有資料時的主內容 -->
    <div v-else-if="hasContent">
      <!-- 路徑 + 檢視模式切換 -->
      <div class="d-flex align-items-center mb-3">
        <div class="flex-grow-1">
          <!-- 麵包屑：只有在不是 root 時顯示 -->
          <nav
            v-if="currentPath.length"
            aria-label="breadcrumb"
          >
            <ol class="breadcrumb breadcrumb-sm mb-0">
              <li class="breadcrumb-item">
                <button
                  type="button"
                  class="btn btn-link p-0 text-decoration-none"
                  @click="goToRoot"
                >
                  {{ $t('downloads.allFolders') }}
                </button>
              </li>

              <li
                v-for="(segment, index) in currentPath"
                :key="segment + '-' + index"
                class="breadcrumb-item"
                :class="{ active: index === currentPath.length - 1 }"
                :aria-current="index === currentPath.length - 1 ? 'page' : null"
              >
                <template v-if="index < currentPath.length - 1">
                  <button
                    type="button"
                    class="btn btn-link p-0 text-decoration-none"
                    @click="goToLevel(index)"
                  >
                    {{ segment }}
                  </button>
                </template>
                <template v-else>
                  {{ segment }}
                </template>
              </li>
            </ol>
          </nav>
        </div>

        <!-- 右側工具列（多選 + 檢視切換） -->
        <div class="toolbar-right ms-2">
          <!-- 多選開關 + 批次下載 -->
          <!-- 多選開關 + 批次下載（遮罩式） -->
          <div
            class="multi-shell"
            :class="{
              'is-open': uiOpen,
              'is-closing': uiClosing
            }"
            role="group"
            aria-label="Multi select"
          >
            <!-- 底層：兩顆按鈕（會被主按鈕蓋住） -->
            <div class="multi-underlay">
              <button
                type="button"
                class="btn btn-success btn-bulk-download multi-u-btn"
                @click="bulkDownloadSelected"
                :disabled="bulkDownloading || !multiSelect"
              >
                <span
                  v-if="bulkDownloading"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                {{ $t('downloads.download') }}
              </button>

              <button
                type="button"
                class="btn btn-outline-primary multi-u-btn"
                @click="toggleSelectAllCurrent"
                :disabled="!multiSelect"
              >
                {{ isAllCurrentSelected ? $t('downloads.cancel') : $t('downloads.selectAll') }}
              </button>
            </div>

            <!-- 上層：主按鈕（遮罩，會縮成箭頭 / 放大蓋回去） -->
            <button
              type="button"
              class="btn multi-main-btn"
              :class="(uiOpen || uiClosing) ? 'btn-primary' : 'btn-primary'"
              @click="toggleMultiSelect"
              :disabled="uiAnimating"
            >
              <span class="icon-stack" aria-hidden="true">
                <span class="material-symbols-outlined icon icon-checklist">checklist</span>
                <span class="material-symbols-outlined icon icon-chevron">chevron_right</span>
              </span>

              <span class="main-label ms-1">
                {{ $t('downloads.mutipledownload') }}
              </span>
            </button>
          </div>
        </div>

        <!-- 檢視模式切換：卡片 / 清單 -->
        <div
          class="view-toggle btn-group btn-group-sm ms-2"
          role="group"
          aria-label="View mode"
        >
          <button
            type="button"
            class="btn btn-outline-secondary"
            :class="{ active: viewMode === 'card' }"
            @click="viewMode = 'card'"
          >
            <span class="material-symbols-outlined align-middle">
              view_comfy_alt
            </span>
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            :class="{ active: viewMode === 'list' }"
            @click="viewMode = 'list'"
          >
            <span class="material-symbols-outlined align-middle">
              menu
            </span>
          </button>
        </div>
      </div>

      <!-- 目前節點的「子資料夾」 -->
      <div v-if="currentNode.children && currentNode.children.length">
        <h6 class="text-muted small mb-2 text-start">
          {{ $t('downloads.folder') }}
        </h6>

        <!-- 清單模式：資料夾 list -->
        <ul v-if="viewMode === 'list'" class="list-group">
          <li
            v-for="folder in currentNode.children"
            :key="folderKey(folder)"
            class="list-group-item d-flex align-items-center clickable-row text-start"
            @click="onFolderClick(folder)"
            :class="{ selected: multiSelect && isSelectedFolder(folder) }"
          >
            <span class="material-symbols-outlined fs-5 folder-icon">folder</span>
            <div class="flex-grow-1">
              <div class="fw-semibold">
                {{ folder.name }}
              </div>
              <div class="small text-muted">
                {{ sectionFileCount(folder) }} {{ $t('downloads.items') }}
              </div>
            </div>
            <span class="material-symbols-outlined ms-2 fs-5 folder-arrow">
              arrow_right
            </span>
          </li>
        </ul>

        <!-- 卡片模式：資料夾 card grid -->
        <div v-else class="row g-4">
          <div
            v-for="folder in currentNode.children"
            :key="folderKey(folder)"
            class="col-6 col-md-4"
          >
            <div
              class="card h-100 shadow-sm border-0 folder-card clickable-row"
              @click="onFolderClick(folder)"
              :class="{ selected: multiSelect && isSelectedFolder(folder) }"
            >
              <div class="card-body d-flex align-items-center text-start">
                <span class="material-symbols-outlined fs-4 folder-icon">
                  folder
                </span>
                <div class="flex-grow-1">
                  <div class="fw-semibold text-truncate">
                    {{ folder.name }}
                  </div>
                  <div class="small text-muted">
                    {{ sectionFileCount(folder) }} {{ $t('downloads.items') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 目前節點底下「直接的檔案」 -->
      <div
        v-if="currentNode.files && currentNode.files.length"
        class="mt-3"
      >
        <h6 class="text-muted small mb-2 text-start">
          {{ $t('downloads.files') }}
        </h6>

        <!-- 檔案清單模式 -->
        <div v-if="viewMode === 'list'" class="card">
        <div class="list-group list-group-flush">
          <div
            v-for="(file, index) in currentNode.files"
            :key="file.id"
            class="list-group-item list-group-item-action d-flex align-items-center file-row text-start"
            @click="onFileClick(file)"
            :class="{ selected: multiSelect && isSelectedFile(file) }"
          >
            <!-- 🔢 流水編號 -->
            <span class="file-serial me-3">
              {{ formatSerial(index) }}
            </span>

            <span class="material-symbols-outlined fs-5 file-icon me-2">
              {{ getFileIcon(file) }}
            </span>
            <div class="flex-grow-1 text-start">
              <div class="fw-semibold">
                {{ file.name }}
              </div>
              <div
                v-if="file.description"
                class="text-muted small mt-1"
              >
                {{ file.description }}
              </div>
              <div class="small text-muted mt-1">
                {{ $t('downloads.createdAt') }} {{ formatDate(file.created_at) }}
              </div>
            </div>
            <button
              type="button"
              class="btn btn-sm"
              :class="multiSelect ? 'btn-outline-primary' : 'btn-primary'"
              @click.stop="multiSelect ? toggleSelectFile(file) : downloadFile(file)"
              :disabled="(!multiSelect && downloadingId === file.id)"
            >
              <span
                v-if="!multiSelect && downloadingId === file.id"
                class="spinner-border spinner-border-sm me-1"
                role="status"
              ></span>
              {{ multiSelect ? (isSelectedFile(file) ? '已選' : '選取') : $t('downloads.download') }}
            </button>
          </div>
        </div>
      </div>


        <!-- 檔案卡片模式 -->
        <div v-else>
          <div class="row g-3">
            <div
              v-for="(file, index) in currentNode.files"
              :key="file.id"
              class="col-12 col-md-6"
            >
              <div
                class="card h-100 shadow-sm border-0 file-card"
                @click="onFileClick(file)"
                :class="{ selected: multiSelect && isSelectedFile(file) }"
              >
                <!-- 卡片左上角小編號 -->
                <div class="file-card-serial">
                  {{ formatSerial(index) }}
                </div>

                <!-- 圖片預覽：thum 優先 -->
                <div
                  v-if="cardThumbSrc(file)"
                  class="file-card-thumb"
                >
                  <img
                    :src="cardThumbSrc(file)"
                    :alt="file.name"
                    class="file-card-thumb-img"
                    loading="lazy"
                    @error="onThumbError(file)"
                  >
                </div>


                <!-- PDF 預覽 -->
                <div
                  v-else-if="file.previewUrl && file.previewType === 'pdf'"
                  class="file-card-thumb"
                >
                  <iframe
                    :src="file.previewUrl"
                    class="file-card-thumb-iframe"
                  ></iframe>
                </div>

                <!-- 其他類型：icon -->
                <div
                  v-else
                  class="file-card-thumb file-card-thumb-placeholder d-flex align-items-center justify-content-center"
                >
                  <span class="material-symbols-outlined fs-1 text-muted">
                    {{ getFileIcon(file) }}
                  </span>
                </div>

                <!-- 下方文字區 -->
                <div class="card-body d-flex flex-column text-start">
                  <div class="fw-semibold mb-1 text-truncate">
                    {{ file.name }}
                  </div>

                  <div
                    v-if="file.description"
                    class="text-muted small mb-2"
                  >
                    {{ file.description }}
                  </div>

                  <div class="mt-auto d-flex justify-content-between align-items-center">
                    <small class="text-muted small">
                      {{ $t('downloads.createdAt') }} {{ formatDate(file.created_at) }}
                    </small>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="multiSelect ? 'btn-outline-primary' : 'btn-primary'"
                      @click.stop="multiSelect ? toggleSelectFile(file) : downloadFile(file)"
                      :disabled="(!multiSelect && downloadingId === file.id)"
                    >
                      <span
                        v-if="!multiSelect && downloadingId === file.id"
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                      {{ multiSelect ? (isSelectedFile(file) ? '已選' : '選取') : $t('downloads.download') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 真的都沒有 -->
      <div
        v-if="!currentNode.children.length && !currentNode.files.length"
        class="text-muted small mt-3 text-start"
      >
        {{ $t('downloads.noFilesHere') }}
      </div>
    </div>

    <!-- 空資料狀態 -->
    <div v-else class="text-muted text-center py-5">
      {{ $t('downloads.empty') }}
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'


export default {
  name: 'Download',

  data () {
    return {
      loading: false,
      error: null,
      files: [],
      downloadingId: null,
      viewMode: 'card', // 'list' | 'card'
      currentPath: [],   // 例如 ['輔銷物', '尖針', 'TSK STERiJECT']

      multiSelect: false,
      selected: new Set(), // 存 key（檔案用 id，資料夾用 path key）
      bulkDownloading: false,
      multiUiClosing: false,
      closingPop: false,
      uiOpen: false,      // 主按鈕縮成箭頭（開啟狀態）
      uiClosing: false,   // 關閉動畫期間（主按鈕放大遮罩中）
      uiAnimating: false  // 避免連點

    }
  },

  computed: {
    // 把 font_path 變成一棵樹：不限層數
    treeRoot () {
      const root = { name: null, children: [], files: [] }

      const ensureChild = (node, name) => {
        let child = node.children.find(c => c.name === name)
        if (!child) {
          child = { name, children: [], files: [] }
          node.children.push(child)
        }
        return child
      }

      this.files.forEach(file => {
        const raw = file.font_path || ''
        const segments = raw
          .split('/')
          .map(s => s.trim())
          .filter(Boolean)

        if (!segments.length) {
          // 沒有 font_path → 掛在 root.files
          root.files.push(file)
          return
        }

        let node = root
        segments.forEach(seg => {
          node = ensureChild(node, seg)
        })
        node.files.push(file)
      })

      // 排序：所有層級的 children 都用中文排序
      const sortNode = (node) => {
        node.children.sort((a, b) =>
          a.name.localeCompare(b.name, 'zh-Hant')
        )
        node.children.forEach(sortNode)
      }
      sortNode(root)

      return root
    },

    // 目前所在節點
    currentNode () {
      let node = this.treeRoot
      for (const seg of this.currentPath) {
        const next = node.children.find(c => c.name === seg)
        if (!next) break
        node = next
      }
      return node
    },

    // 判斷是否有任何資料
    hasContent () {
      const root = this.treeRoot
      return !!(root.children.length || root.files.length)
    },

    currentSelectableFolderKeys () {
      const folders = this.currentNode.children || []
      return folders.map(f => this.folderSelectKey(f))
    },

    currentSelectableFileKeys () {
      const files = this.currentNode.files || []
      return files.map(f => this.fileSelectKey(f))
    },

    isAllCurrentSelected () {
      const keys = [...this.currentSelectableFolderKeys, ...this.currentSelectableFileKeys]
      if (!keys.length) return false
      return keys.every(k => this.selected.has(k))
    }
  },

  methods: {
    formatSerial (index) {
      const n = index + 1
      // 變成 001 / 002 / 010 / 123 這種格式
      return n.toString().padStart(3, '0')
    },
    // 產預覽 URL（跟你原本一樣）
    async fetchFiles () {
      this.loading = true
      this.error = null
      try {
        const { data, error } = await supabase
          .from('file_library')
          .select('*')
          .eq('is_active', true)
          .order('font_path', { ascending: true })
          .order('sort_order', { ascending: true })

        if (error) throw error

        const rawFiles = data || []

        const filesWithPreview = await Promise.all(
          rawFiles.map(async (file) => {
            let previewUrl = ''
            let previewType = '' // 'image' | 'pdf' | ''

            const isImage = this.isImageFile(file)
            const isPdf = this.isPdfFile(file)

            if (isImage || isPdf) {
              try {
                const { data: signedData, error: signedError } = await supabase
                  .storage
                  .from(file.bucket)
                  .createSignedUrl(file.bg_path, 3600)

                if (!signedError && signedData?.signedUrl) {
                  previewUrl = signedData.signedUrl
                  previewType = isImage ? 'image' : 'pdf'
                } else if (signedError) {
                  console.warn('preview signed url error', signedError)
                }
              } catch (e) {
                console.warn('preview signed url exception', e)
              }
            }

            return {
              ...file,
              previewUrl,
              previewType
            }
          })
        )

        this.files = filesWithPreview
        this.currentPath = []
      } catch (err) {
        console.error(err)
        this.error = '載入下載資料時發生錯誤，稍後再試一次。'
      } finally {
        this.loading = false
      }
    },

    // 進入子資料夾
    openFolder (folder) {
      if (!folder || !folder.name) return
      this.currentPath = [...this.currentPath, folder.name]
    },

    // 回到根目錄
    goToRoot () {
      this.currentPath = []
    },

    // 點麵包屑中間某一層
    goToLevel (index) {
      this.currentPath = this.currentPath.slice(0, index + 1)
    },

    // 統一算「這個 folder 底下總共有多少檔案（含子孫）」
    sectionFileCount (node) {
      const countNode = (n) => {
        const self = (n.files ? n.files.length : 0)
        const children = (n.children || []).reduce((sum, c) => sum + countNode(c), 0)
        return self + children
      }
      return countNode(node)
    },

    folderKey (folder) {
      // name 在同一層是不會重複，當 key 足夠
      return folder.name
    },

    fileExtension (path) {
      if (!path) return ''
      const parts = path.split('.')
      return parts.length > 1 ? parts.pop() : ''
    },

    formatDate (value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    getFileIcon (file) {
      const ext = (this.fileExtension(file.bg_path) || '').toLowerCase()

      if (ext === 'pdf') {
        return 'picture_as_pdf'
      }

      if (ext === 'ppt' || ext === 'pptx') {
        return 'description'
      }

      if (['mp4', 'mov', 'avi', 'mkv', 'm4v', 'webm'].includes(ext)) {
        return 'movie'
      }

      return 'description'
    },

    isImageFile (file) {
      const ext = (this.fileExtension(file.bg_path) || '').toLowerCase()
      return ['jpg', 'jpeg', 'png'].includes(ext)
    },

    isPdfFile (file) {
      const ext = (this.fileExtension(file.bg_path) || '').toLowerCase()
      return ext === 'pdf'
    },

    async downloadFile (file) {
      this.error = null
      this.downloadingId = file.id
      try {
        await this.downloadFileSignedOpen(file)
      } catch (err) {
        console.error(err)
        this.error = '下載失敗，請稍後再試一次。'
      } finally {
        this.downloadingId = null
      }
    },

    cardThumbSrc (file) {
      // 1) 有 thum 就用 thum（你後台給的縮圖 URL）
      const th = (file.thum || '').trim()
      if (th) return th

      // 2) 沒 thum 才用你原本的預覽（限圖片）
      if (file.previewUrl && file.previewType === 'image') return file.previewUrl

      // 其他情況就不顯示圖片區塊（會走下面的 pdf / icon）
      return ''
    },

    onThumbError (file) {
      // 如果 thum 壞掉：清掉 thum，讓它回退到 previewUrl 或 icon
      // Vue 2 要用 $set 才保證響應式更新
      this.$set(file, 'thum', '')
    },

    // ---- 多選 key 生成 ----
    folderSelectKey (folder) {
      // 用目前路徑 + folder.name 生成唯一 key（同名資料夾不同層也不會撞）
      const path = [...this.currentPath, folder.name].join('/')
      return `folder:${path}`
    },
    fileSelectKey (file) {
      return `file:${file.id}`
    },

    isSelectedFolder (folder) {
      return this.selected.has(this.folderSelectKey(folder))
    },
    isSelectedFile (file) {
      return this.selected.has(this.fileSelectKey(file))
    },

    toggleSelectFolder (folder) {
      const k = this.folderSelectKey(folder)
      if (this.selected.has(k)) this.selected.delete(k)
      else this.selected.add(k)
    },
    toggleSelectFile (file) {
      const k = this.fileSelectKey(file)
      if (this.selected.has(k)) this.selected.delete(k)
      else this.selected.add(k)
    },

    // ---- 點擊行為分流：多選 vs 原本行為 ----
    onFolderClick (folder) {
      if (this.multiSelect) return this.toggleSelectFolder(folder)
      return this.openFolder(folder)
    },
    onFileClick (file) {
      if (this.multiSelect) return this.toggleSelectFile(file)
      return this.downloadFile(file)
    },

    async toggleMultiSelect () {
      if (this.uiAnimating) return
      this.uiAnimating = true

      // ===== 開啟：文字按鈕 → 從右往左縮成箭頭，底下兩顆滑出 =====
      if (!this.uiOpen && !this.uiClosing) {
        this.selected.clear()
        this.multiSelect = true
        this.uiOpen = true

        // 動畫時間跟 CSS 對齊（下面 CSS 我用 260ms）
        await new Promise(resolve => setTimeout(resolve, 260))
        this.uiAnimating = false
        return
      }

      // ===== 關閉：箭頭 → 從左往右放大，把兩顆依序蓋住，最後才回文字 =====
      this.multiSelect = false
      this.uiClosing = true
      this.uiOpen = false

      await new Promise(resolve => setTimeout(resolve, 260))

      this.uiClosing = false
      this.selected.clear()
      this.uiAnimating = false
    },

    clearSelection () {
      this.selected.clear()
    },

    // 取得「本層」已選檔案（不含資料夾）
    selectedFilesInCurrentNode () {
      const files = this.currentNode.files || []
      return files.filter(f => this.isSelectedFile(f))
    },

    async bulkDownloadSelected () {
      this.error = null

      const selectedFiles = this.getSelectedFiles()
      if (!selectedFiles.length) {
        this.error = '你還沒選任何檔案或資料夾。'
        return
      }

      this.bulkDownloading = true
      try {
        const zip = new JSZip()

        // 用來處理 zip 內同名檔案（避免覆蓋）
        const usedPaths = new Map()
        const makeUniquePath = (p) => {
          const n = usedPaths.get(p) || 0
          usedPaths.set(p, n + 1)
          if (n === 0) return p
          // foo.pdf -> foo (2).pdf
          const dot = p.lastIndexOf('.')
          if (dot === -1) return `${p} (${n + 1})`
          return `${p.slice(0, dot)} (${n + 1})${p.slice(dot)}`
        }

        // 逐檔加入 zip（可再做並行，但先用序列最穩）
        for (const file of selectedFiles) {
          const ext = (this.fileExtension(file.bg_path) || '').toLowerCase()

          // 讓 zip 內檔名有副檔名（用 bg_path 的 ext）
          const baseName = (file.name || 'file').replace(/\.[^.]+$/, '')
          const fileNameWithExt = ext ? `${baseName}.${ext}` : baseName

          // 保留資料夾結構：font_path/檔名
          const folderPath = (file.font_path || '').trim()
          const zipPathRaw = folderPath ? `${folderPath}/${fileNameWithExt}` : fileNameWithExt
          const zipPath = makeUniquePath(zipPathRaw)

          // 取 signed url（時間拉長一點）
          const { data, error } = await supabase
            .storage
            .from(file.bucket)
            .createSignedUrl(file.bg_path, 600)

          if (error) throw error

          // 抓檔案內容塞進 zip
          const resp = await fetch(data.signedUrl)
          if (!resp.ok) throw new Error(`Fetch failed (${resp.status}) ${zipPath}`)
          const buf = await resp.arrayBuffer()

          zip.file(zipPath, buf)
        }

        // 產出 zip blob 並下載一次
        const blob = await zip.generateAsync({ type: 'blob' })
        saveAs(blob, 'downloads.zip')
      } catch (e) {
        console.error(e)
        this.error = 'zip 打包失敗，請稍後再試一次。'
      } finally {
        this.bulkDownloading = false
      }
    },

    // 把 downloadFile 的「產 signed url + 觸發下載」拆成可重用（不動 downloadingId）
    async downloadFileSignedOpen (file) {
      const ext = this.fileExtension(file.bg_path) || ''
      const filename = ext
        ? `${file.name.replace(/\.[^.]+$/, '')}.${ext}`
        : file.name

      const { data, error } = await supabase
        .storage
        .from(file.bucket)
        .createSignedUrl(file.bg_path, 60, { download: filename })

      if (error) throw error
      window.location.href = data.signedUrl

      // 給瀏覽器一點時間處理下載
      await new Promise(resolve => setTimeout(resolve, 150))
    },

    // 找某個路徑對應的 node（path 形如 ['輔銷物','尖針']）
    getNodeByPathSegments (segments) {
      let node = this.treeRoot
      for (const seg of segments) {
        const next = (node.children || []).find(c => c.name === seg)
        if (!next) return null
        node = next
      }
      return node
    },

    // 收集某個 node 底下所有檔案（含子孫）
    collectFilesRecursively (node) {
      if (!node) return []
      const selfFiles = node.files || []
      const childFiles = (node.children || []).flatMap(c => this.collectFilesRecursively(c))
      return [...selfFiles, ...childFiles]
    },

    // 從 selected keys 解析出「所有要打包的檔案」
    getSelectedFiles () {
      const fileMap = new Map(this.files.map(f => [String(f.id), f]))
      const result = []
      const seen = new Set()

      for (const key of this.selected) {
        // 1) 直接選檔案
        if (key.startsWith('file:')) {
          const id = key.slice('file:'.length)
          const f = fileMap.get(String(id))
          if (f && !seen.has(f.id)) {
            seen.add(f.id)
            result.push(f)
          }
          continue
        }

        // 2) 選資料夾：folder:輔銷物/尖針/TSK
        if (key.startsWith('folder:')) {
          const path = key.slice('folder:'.length)
          const segments = path.split('/').filter(Boolean)
          const node = this.getNodeByPathSegments(segments)
          const files = this.collectFilesRecursively(node)
          for (const f of files) {
            if (f && !seen.has(f.id)) {
              seen.add(f.id)
              result.push(f)
            }
          }
        }
      }

      return result
    },
    toggleSelectAllCurrent () {
      const keys = [
        ...(this.currentNode.children || []).map(f => this.folderSelectKey(f)),
        ...(this.currentNode.files || []).map(f => this.fileSelectKey(f))
      ]

      if (!keys.length) return

      if (keys.every(k => this.selected.has(k))) {
        // 全部已選 → 取消本頁全選
        keys.forEach(k => this.selected.delete(k))
      } else {
        // 未全選 → 全部選起來
        keys.forEach(k => this.selected.add(k))
      }
    }
  },

  mounted () {
    this.fetchFiles()
  }
}
</script>

<style scoped>
.download-page {
  max-width: 900px;
  margin: 0 auto;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: #f8f9fa;
}

.file-row {
  cursor: pointer;
}

.file-row:disabled {
  cursor: default;
  opacity: 0.7;
}

.breadcrumb-sm {
  font-size: 0.85rem;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
}

.breadcrumb-item button {
  font-size: inherit;
  line-height: inherit;
  vertical-align: baseline;
  text-decoration: none;
}

/* 檢視模式切換按鈕 */
.view-toggle .btn {
  padding: 0.15rem 0.4rem;
}

.view-toggle .material-symbols-outlined {
  font-size: 20px;
}

/* 資料夾箭頭 */
.folder-arrow {
  color: #adb5bd;
}

/* icon 對齊 */
.folder-icon,
.file-icon {
  margin-right: 1rem !important;
  color: #6c757d;
}

/* 卡片 hover 效果 */
.folder-card,
.file-card {
  transition: box-shadow 0.15s ease, transform 0.05s ease;
}

.folder-card:hover,
.file-card:hover {
  box-shadow: 0 0.35rem 1rem rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.file-thumb {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f8f9fa;
}

.file-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 卡片上方的大圖預覽 */
.file-card-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #f8f9fa;
}

.file-card-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 非圖片檔案的灰色 placeholder */
.file-card-thumb-placeholder {
  color: #adb5bd;
}

.file-card-thumb-iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

/* 讓所有卡片模式的 row 間距拉大 */
.row.g-3 {
  --bs-gutter-x: 3rem; /* 左右間距加大 */
  --bs-gutter-y: 3rem; /* 上下間距加大 */
}

/* 多選：選取狀態 */
.selected {
  background-color: #e7f1ff !important; /* 淡藍 */
  outline: 2px solid rgba(13, 110, 253, 0.35); /* bootstrap primary 的氣氛 */
}

/* 卡片選取時也要明顯 */
.folder-card.selected,
.file-card.selected {
  box-shadow: 0 0.6rem 1.25rem rgba(13, 110, 253, 0.18);
  transform: translateY(-1px);
}
/* 全部下載：避免白字白底（被全域樣式覆蓋時） */
.btn-bulk-download {
  color: #fff !important;
  background-color: #198754 !important; /* bootstrap success */
  border-color: #198754 !important;
}

/* hover / focus 也固定一下 */
.btn-bulk-download:hover,
.btn-bulk-download:focus {
  color: #fff !important;
  background-color: #157347 !important;
  border-color: #146c43 !important;
}

/* disabled 狀態也要可讀 */
.btn-bulk-download:disabled {
  color: rgba(255, 255, 255, 0.9) !important;
  background-color: #198754 !important;
  border-color: #198754 !important;
  opacity: 0.65;
}

/* ===== 遮罩式多選工具列 ===== */
.multi-shell {
  --main-open-w: 40px;         /* 箭頭按鈕寬度（可微調 36~44） */
  height: 32px;
  position: relative;
  display: inline-block;
  vertical-align: middle;
}

/* 底層：兩顆按鈕（正常排版決定整組寬度） */
.multi-underlay {
  height: 100%;
  display: inline-flex;
  align-items: center;
  padding-left: var(--main-open-w); /* 預留箭頭位置 */
}

/* 讓底層按鈕高度一致 */
.multi-underlay .multi-u-btn {
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  line-height: 1;
  white-space: nowrap;
}

/* 底層按鈕圓角：中間接縫要方、右側要圓 */
.multi-underlay .multi-u-btn:first-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.multi-underlay .multi-u-btn:last-child {
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
}

/* 底層按鈕：永遠不透明，只做滑動 */
.multi-underlay .multi-u-btn {
  opacity: 1; /* ✅ 永遠 1 */
  transform: translateX(-10px);
  transition: transform 220ms cubic-bezier(.2,.8,.2,1);
  pointer-events: none; /* 關閉時不能點 */
}

/* 開啟/關閉動畫期間：滑到定位 */
.multi-shell.is-open .multi-underlay .multi-u-btn,
.multi-shell.is-closing .multi-underlay .multi-u-btn {
  transform: translateX(0);
}

/* 只有真正開啟才允許點 */
.multi-shell.is-open .multi-underlay .multi-u-btn {
  pointer-events: auto;
}


/* 只有真正開啟狀態才允許點底層按鈕 */
.multi-shell.is-open .multi-underlay .multi-u-btn {
  pointer-events: auto;
}

/* 開啟時兩顆按鈕稍微依序滑出 */
.multi-shell.is-open .multi-underlay .multi-u-btn:nth-child(1) { transition-delay: 40ms; }
.multi-shell.is-open .multi-underlay .multi-u-btn:nth-child(2) { transition-delay: 90ms; }

/* 上層遮罩：主按鈕 */
.multi-main-btn {
  position: absolute;
  top: 0;
  left: 0;
  height: 32px;
  width: 100%;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  border-radius: 0.5rem;
  padding: 0 10px;

  transition: width 260ms cubic-bezier(.2,.8,.2,1), padding 260ms cubic-bezier(.2,.8,.2,1);
  will-change: width;
}

/* 開啟：遮罩縮成箭頭（右側露出底層） */
.multi-shell.is-open .multi-main-btn {
  width: var(--main-open-w);
  padding: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* icon 疊在一起做切換 */
.icon-stack {
  position: relative;
  width: 20px;
  height: 20px;
  display: inline-block;
}

.icon-stack .icon {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
  transition: opacity 160ms ease, transform 220ms cubic-bezier(.2,.8,.2,1);
}

/* 預設顯示 checklist */
.icon-chevron { opacity: 0; transform: translateX(-3px); }
.icon-checklist { opacity: 1; transform: translateX(0); }

/* 開啟 or 關閉動畫期間：顯示箭頭 */
.multi-shell.is-open .icon-chevron,
.multi-shell.is-closing .icon-chevron {
  opacity: 1;
  transform: translateX(0);
}
.multi-shell.is-open .icon-checklist,
.multi-shell.is-closing .icon-checklist {
  opacity: 0;
  transform: translateX(3px);
}

/* 主按鈕文字：只有完全關閉時才可見（關閉動畫中也要隱藏） */
.main-label {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  max-width: 180px;
  opacity: 1;
  transition: opacity 160ms ease, max-width 220ms cubic-bezier(.2,.8,.2,1);
}

.multi-shell.is-open .main-label,
.multi-shell.is-closing .main-label {
  max-width: 0;
  opacity: 0;
}

/* ✅ 保險：確保 Material Symbols 在 scoped 內一定能顯示 */
.material-symbols-outlined {
  font-family: "Material Symbols Outlined" !important;
  font-weight: normal !important;
  font-style: normal !important;
  font-size: 20px;
  line-height: 1 !important;
  letter-spacing: normal !important;
  text-transform: none !important;
  display: inline-flex !important;
  white-space: nowrap !important;
  word-wrap: normal !important;
  direction: ltr !important;
  -webkit-font-smoothing: antialiased !important;

  /* Material Symbols 關鍵：字重/填滿/粗細設定 */
  font-variation-settings:
    "FILL" 0,
    "wght" 400,
    "GRAD" 0,
    "opsz" 24 !important;
}

/* ✅ 避免 icon-stack 被按鈕樣式壓扁 */
.icon-stack {
  flex: 0 0 20px;
}
/* ✅ 主按鈕：按下/聚焦時不要有「變透明」效果 */
.multi-main-btn,
.multi-main-btn:focus,
.multi-main-btn:active,
.multi-main-btn:focus:active {
  opacity: 1 !important;
  filter: none !important;
  box-shadow: none !important; /* 如果你想保留 focus ring 可移除這行 */
}

/* ✅ 防止 bootstrap 的 active 讓顏色變淡（尤其 btn-primary） */
.multi-main-btn.btn-primary:active,
.multi-main-btn.btn-primary.active {
  background-color: #0d6efd !important; /* bootstrap primary */
  border-color: #0d6efd !important;
}

/* ✅ 如果你有用到 btn-outline（現在你是 btn-primary，但留著也不虧） */
.multi-main-btn.btn-outline-primary:active,
.multi-main-btn.btn-outline-primary.active {
  opacity: 1 !important;
  background-color: rgba(13, 110, 253, 0.12) !important;
  border-color: #0d6efd !important;
  color: #0d6efd !important;
}

/* ✅ iOS/Safari 會有點擊高亮，順便拔掉（看起來也像透明） */
.multi-main-btn {
  -webkit-tap-highlight-color: transparent;
}

</style>
